import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import {
  DollarSign,
  Package,
  Clock,
  CheckCircle,
  XCircle,
  Truck,
  Eye,
  Plus,
  ArrowLeft,
  Loader2,
  ShoppingBag,
  TrendingUp,
  Users,
  CalendarDays,
  Trash2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORM_NAMES = {
  telegram: "Telegram", privacy: "Privacy", onlyfans: "OnlyFans",
  instagram: "Instagram", twitter: "Twitter/X", tiktok: "TikTok", closefans: "CloseFans",
};

const FORMAT_OPTIONS = [
  "Post no feed", "Post fixado", "Story/Status",
  "Menção na bio", "Mensagem em massa", "Link direto",
];

const STATUS_CONFIG = {
  pending: { label: "Aguardando", color: "bg-yellow-500/20 text-yellow-400", icon: Clock },
  paid: { label: "Comprovante Enviado", color: "bg-blue-500/20 text-blue-400", icon: DollarSign },
  confirmed: { label: "Pagamento Confirmado", color: "bg-emerald-500/20 text-emerald-400", icon: CheckCircle },
  scheduled: { label: "Agendado", color: "bg-violet-500/20 text-violet-400", icon: CalendarDays },
  delivered: { label: "Entregue", color: "bg-green-500/20 text-green-400", icon: Truck },
  payment_rejected: { label: "Comprovante Recusado", color: "bg-red-500/20 text-red-400", icon: XCircle },
  cancelled: { label: "Cancelado", color: "bg-white/10 text-white/40", icon: XCircle },
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");
  const [proofModal, setProofModal] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [scheduleModal, setScheduleModal] = useState(null);
  const [scheduleItems, setScheduleItems] = useState([]);
  const [submittingSchedule, setSubmittingSchedule] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, ordersRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats?Authorization=Bearer ${token}`),
        axios.get(`${API}/services/orders/received?Authorization=Bearer ${token}`),
      ]);
      setStats(statsRes.data);
      setOrders(ordersRes.data.orders || []);
    } catch (error) {
      toast.error("Erro ao carregar dashboard");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleConfirmPayment = async (orderId) => {
    setActionLoading(orderId);
    try {
      await axios.put(`${API}/orders/${orderId}/confirm-payment?Authorization=Bearer ${token}`);
      toast.success("Pagamento confirmado!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao confirmar");
    } finally { setActionLoading(null); }
  };

  const handleRejectPayment = async (orderId) => {
    setActionLoading(orderId);
    try {
      await axios.put(`${API}/orders/${orderId}/reject-payment?Authorization=Bearer ${token}`);
      toast.success("Comprovante recusado");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao recusar");
    } finally { setActionLoading(null); }
  };

  const handleMarkDelivered = async (orderId) => {
    setActionLoading(orderId);
    try {
      await axios.put(`${API}/orders/${orderId}/mark-delivered?Authorization=Bearer ${token}`);
      toast.success("Marcado como entregue!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro");
    } finally { setActionLoading(null); }
  };

  // Schedule functions
  const openScheduleModal = (order) => {
    const servicePlatforms = order.service?.platforms || [order.service?.platform].filter(Boolean);
    const serviceIncludes = order.service?.includes || [];
    setScheduleModal(order);
    setScheduleItems([{
      format: serviceIncludes[0] || FORMAT_OPTIONS[0],
      platform: servicePlatforms[0] || "",
      start_date: "",
      end_date: "",
    }]);
  };

  const addScheduleItem = () => {
    const servicePlatforms = scheduleModal?.service?.platforms || [scheduleModal?.service?.platform].filter(Boolean);
    setScheduleItems([...scheduleItems, {
      format: FORMAT_OPTIONS[0],
      platform: servicePlatforms[0] || "",
      start_date: "",
      end_date: "",
    }]);
  };

  const updateScheduleItem = (index, field, value) => {
    const updated = [...scheduleItems];
    updated[index][field] = value;
    setScheduleItems(updated);
  };

  const removeScheduleItem = (index) => {
    setScheduleItems(scheduleItems.filter((_, i) => i !== index));
  };

  const handleSaveSchedule = async () => {
    if (!scheduleModal) return;
    const valid = scheduleItems.every((item) => item.start_date && item.end_date);
    if (!valid) {
      toast.error("Preencha todas as datas de início e fim");
      return;
    }
    setSubmittingSchedule(true);
    try {
      await axios.put(
        `${API}/orders/${scheduleModal.id}/schedule?Authorization=Bearer ${token}`,
        { items: scheduleItems }
      );
      toast.success("Agendamento salvo! O comprador foi notificado.");
      setScheduleModal(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao agendar");
    } finally { setSubmittingSchedule(false); }
  };

  const getProofUrl = (url) => {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  const filteredOrders = tab === "pending"
    ? orders.filter((o) => ["pending", "paid", "confirmed", "scheduled"].includes(o.status))
    : orders;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#080808] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FD5564]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080808] text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 px-4 py-4 bg-[#080808]/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/home")} className="text-white/70" data-testid="dashboard-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-lg font-bold">Dashboard de Vendas</h1>
          </div>
          <Button size="sm" className="bg-gradient-to-r from-[#FD5564] to-[#FF7854] hover:opacity-90 text-white" onClick={() => navigate("/home")} data-testid="new-package-btn">
            <Plus className="w-4 h-4 mr-1" /> Novo Pacote
          </Button>
        </div>
      </header>

      <main className="px-4 py-6">
        <div className="max-w-4xl mx-auto">
          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
              {[
                { icon: Package, value: stats.total_services, label: "Pacotes", cls: "" },
                { icon: ShoppingBag, value: stats.total_orders, label: "Pedidos", cls: "" },
                { icon: Clock, value: stats.pending_orders, label: "Pendentes", cls: "bg-yellow-500/5 border-yellow-500/20", valueCls: "text-yellow-400", iconCls: "text-yellow-400" },
                { icon: TrendingUp, value: `R$ ${stats.total_revenue?.toFixed(0) || 0}`, label: "Faturamento", cls: "bg-emerald-500/5 border-emerald-500/20", valueCls: "text-emerald-400", iconCls: "text-emerald-400" },
              ].map((s, i) => {
                const Icon = s.icon;
                return (
                  <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                    className={`p-4 border rounded-xl ${s.cls || "bg-white/[0.03] border-white/10"}`}>
                    <Icon className={`w-5 h-5 mb-2 ${s.iconCls || "text-white/40"}`} />
                    <div className={`text-2xl font-bold ${s.valueCls || ""}`}>{s.value}</div>
                    <div className="text-white/40 text-sm">{s.label}</div>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-2 mb-4">
            {[
              { id: "pending", label: `Pendentes (${orders.filter((o) => ["pending","paid","confirmed","scheduled"].includes(o.status)).length})` },
              { id: "all", label: `Todos (${orders.length})` },
            ].map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === t.id ? "bg-[#FD5564] text-white" : "bg-white/5 text-white/50 hover:text-white"}`}
                data-testid={`tab-${t.id}`}>
                {t.label}
              </button>
            ))}
          </div>

          {/* Orders List */}
          <div className="space-y-3">
            {filteredOrders.map((order) => {
              const statusCfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
              const StatusIcon = statusCfg.icon;
              const servicePlatforms = order.service?.platforms || [order.service?.platform].filter(Boolean);
              return (
                <motion.div key={order.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  className="p-4 bg-white/[0.03] border border-white/10 rounded-xl" data-testid={`order-card-${order.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <Badge className={`${statusCfg.color} border-0 text-xs`}>
                          <StatusIcon className="w-3 h-3 mr-1" />{statusCfg.label}
                        </Badge>
                        {/* Platform badges */}
                        {servicePlatforms.map((p, pi) => (
                          <span key={pi} className="text-xs px-1.5 py-0.5 bg-white/5 border border-white/10 rounded text-white/50">
                            {PLATFORM_NAMES[p] || p}
                          </span>
                        ))}
                      </div>
                      <h3 className="font-medium">{order.service?.title || "Serviço"}</h3>
                      <div className="flex items-center gap-2 mt-1 text-sm text-white/40">
                        <Users className="w-3.5 h-3.5" />
                        <span>{order.buyer?.display_name || "Comprador"}</span>
                      </div>
                      {order.message && (
                        <p className="text-sm text-white/50 mt-2 bg-white/5 p-2 rounded-lg">"{order.message}"</p>
                      )}
                      {/* Show schedule if exists */}
                      {order.schedule?.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {order.schedule.map((item, si) => (
                            <div key={si} className="text-xs bg-violet-500/10 border border-violet-500/20 rounded-lg px-2 py-1 flex items-center gap-2">
                              <CalendarDays className="w-3 h-3 text-violet-400" />
                              <span className="text-violet-300">{item.format}</span>
                              <span className="text-white/40">({PLATFORM_NAMES[item.platform] || item.platform})</span>
                              <span className="text-white/50">
                                {new Date(item.start_date).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                                {" - "}
                                {new Date(item.end_date).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="text-right ml-4">
                      <div className="text-lg font-bold text-[#FD5564]">R$ {order.price?.toFixed(2).replace(".", ",")}</div>
                      <div className="text-xs text-white/30 mt-1">{new Date(order.created_at).toLocaleDateString("pt-BR")}</div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 flex-wrap">
                    {order.status === "paid" && order.payment_proof_url && (
                      <>
                        <Button size="sm" variant="outline" className="border-white/20 text-white hover:bg-white/10"
                          onClick={() => setProofModal(order)} data-testid={`view-proof-${order.id}`}>
                          <Eye className="w-3.5 h-3.5 mr-1" /> Ver Comprovante
                        </Button>
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={() => handleConfirmPayment(order.id)} disabled={actionLoading === order.id} data-testid={`confirm-payment-${order.id}`}>
                          {actionLoading === order.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><CheckCircle className="w-3.5 h-3.5 mr-1" /> Confirmar Pagamento</>}
                        </Button>
                        <Button size="sm" variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                          onClick={() => handleRejectPayment(order.id)} disabled={actionLoading === order.id} data-testid={`reject-payment-${order.id}`}>
                          <XCircle className="w-3.5 h-3.5 mr-1" /> Recusar
                        </Button>
                      </>
                    )}
                    {order.status === "confirmed" && (
                      <>
                        <Button size="sm" className="bg-violet-600 hover:bg-violet-700 text-white"
                          onClick={() => openScheduleModal(order)} data-testid={`schedule-btn-${order.id}`}>
                          <CalendarDays className="w-3.5 h-3.5 mr-1" /> Agendar Divulgação
                        </Button>
                        <Button size="sm" className="bg-gradient-to-r from-[#FD5564] to-[#FF7854] hover:opacity-90 text-white"
                          onClick={() => handleMarkDelivered(order.id)} disabled={actionLoading === order.id} data-testid={`mark-delivered-${order.id}`}>
                          {actionLoading === order.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Truck className="w-3.5 h-3.5 mr-1" /> Marcar como Entregue</>}
                        </Button>
                      </>
                    )}
                    {order.status === "scheduled" && (
                      <Button size="sm" className="bg-gradient-to-r from-[#FD5564] to-[#FF7854] hover:opacity-90 text-white"
                        onClick={() => handleMarkDelivered(order.id)} disabled={actionLoading === order.id} data-testid={`mark-delivered-${order.id}`}>
                        {actionLoading === order.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Truck className="w-3.5 h-3.5 mr-1" /> Marcar como Entregue</>}
                      </Button>
                    )}
                  </div>
                </motion.div>
              );
            })}

            {filteredOrders.length === 0 && (
              <div className="text-center py-16 text-white/40">
                <ShoppingBag className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="font-medium">{tab === "pending" ? "Nenhum pedido pendente" : "Nenhum pedido ainda"}</p>
                <p className="text-sm mt-1">Seus pedidos de compra aparecerão aqui.</p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Proof Modal */}
      <Dialog open={!!proofModal} onOpenChange={() => setProofModal(null)}>
        <DialogContent className="bg-[#1a1a1a] border-white/10 text-white max-w-lg">
          <DialogHeader><DialogTitle>Comprovante de Pagamento</DialogTitle></DialogHeader>
          {proofModal?.payment_proof_url && (
            <div className="space-y-4">
              <img src={getProofUrl(proofModal.payment_proof_url)} alt="Comprovante" className="w-full rounded-lg border border-white/10" />
              <div className="flex gap-2">
                <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => { handleConfirmPayment(proofModal.id); setProofModal(null); }} data-testid="modal-confirm-btn">
                  <CheckCircle className="w-4 h-4 mr-1" /> Confirmar Pagamento
                </Button>
                <Button variant="outline" className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10"
                  onClick={() => { handleRejectPayment(proofModal.id); setProofModal(null); }} data-testid="modal-reject-btn">
                  <XCircle className="w-4 h-4 mr-1" /> Recusar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Schedule Modal */}
      <Dialog open={!!scheduleModal} onOpenChange={() => setScheduleModal(null)}>
        <DialogContent className="bg-[#1a1a1a] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Agendar Divulgação</DialogTitle>
            <DialogDescription className="text-white/50">
              Defina as datas e formatos de cada divulgação para este pedido.
            </DialogDescription>
          </DialogHeader>
          {scheduleModal && (
            <div className="space-y-4">
              {/* Order summary */}
              <div className="bg-white/5 rounded-xl p-3 text-sm">
                <div className="font-medium">{scheduleModal.service?.title}</div>
                <div className="text-white/50">{scheduleModal.buyer?.display_name} - R$ {scheduleModal.price?.toFixed(2).replace(".",",")}</div>
              </div>

              {/* Schedule items */}
              {scheduleItems.map((item, index) => {
                const servicePlatforms = scheduleModal.service?.platforms || [scheduleModal.service?.platform].filter(Boolean);
                return (
                  <div key={index} className="p-4 bg-white/[0.03] border border-white/10 rounded-xl space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-white/50 font-medium">Divulgação {index + 1}</span>
                      {scheduleItems.length > 1 && (
                        <button onClick={() => removeScheduleItem(index)} className="text-red-400 hover:text-red-300 p-1">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-white/60 text-xs">Formato</Label>
                        <select value={item.format} onChange={(e) => updateScheduleItem(index, "format", e.target.value)}
                          className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm">
                          {FORMAT_OPTIONS.map((f) => (
                            <option key={f} value={f} className="bg-[#1a1a1a]">{f}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label className="text-white/60 text-xs">Plataforma</Label>
                        <select value={item.platform} onChange={(e) => updateScheduleItem(index, "platform", e.target.value)}
                          className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm">
                          {servicePlatforms.map((p) => (
                            <option key={p} value={p} className="bg-[#1a1a1a]">{PLATFORM_NAMES[p] || p}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-white/60 text-xs">Início</Label>
                        <Input type="datetime-local" value={item.start_date} onChange={(e) => updateScheduleItem(index, "start_date", e.target.value)}
                          className="mt-1 bg-white/5 border-white/10 text-white text-sm" data-testid={`schedule-start-${index}`} />
                      </div>
                      <div>
                        <Label className="text-white/60 text-xs">Fim</Label>
                        <Input type="datetime-local" value={item.end_date} onChange={(e) => updateScheduleItem(index, "end_date", e.target.value)}
                          className="mt-1 bg-white/5 border-white/10 text-white text-sm" data-testid={`schedule-end-${index}`} />
                      </div>
                    </div>
                  </div>
                );
              })}

              <button onClick={addScheduleItem}
                className="w-full p-3 border-2 border-dashed border-white/20 rounded-xl text-white/50 hover:text-white hover:border-white/40 transition-colors flex items-center justify-center gap-2 text-sm">
                <Plus className="w-4 h-4" /> Adicionar outro formato
              </button>

              <Button className="w-full bg-violet-600 hover:bg-violet-700 text-white" onClick={handleSaveSchedule}
                disabled={submittingSchedule} data-testid="save-schedule-btn">
                {submittingSchedule ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CalendarDays className="w-4 h-4 mr-2" /> Confirmar Agendamento</>}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardPage;
